import { createContext, type ReactNode, useContext, useRef } from "react"
import { useStore } from "zustand"

import useEnsureCompressedForecastsAreCorrect from "~/hooks/useEnsureCompressedForecastsAreCorrect"
import { createSkuStore, initSkuStore, type SkuStore } from "~/stores/sku-store"

type SkuStoreApi = ReturnType<typeof createSkuStore>

const SkuStoreContext = createContext<SkuStoreApi | undefined>(undefined)

export interface SkuStoreProviderProps {
  children: ReactNode
}

export const SkuStoreProvider = ({ children }: SkuStoreProviderProps) => {
  useEnsureCompressedForecastsAreCorrect()
  const storeRef = useRef<SkuStoreApi>(undefined)
  if (!storeRef.current) {
    storeRef.current = createSkuStore(initSkuStore())
  }

  return <SkuStoreContext.Provider value={storeRef.current}>{children}</SkuStoreContext.Provider>
}

export const useSkuStore = <T,>(selector: (store: SkuStore) => T): T => {
  const skuStoreContext = useContext(SkuStoreContext)

  if (!skuStoreContext) {
    throw new Error(`useSkuStore must be used within SkuStoreProvider`)
  }

  return useStore(skuStoreContext, selector)
}
